import { MediaType } from '../../data/types'

interface EstimateLineItem {
  mediaType: MediaType
  storeCount: number
  unitPrice: number
  subtotal: number
}

interface EstimateData {
  brandName: string
  campaignName: string
  weekNumber: number
  endWeekNumber?: number
  weekLabel: string
  mediaTypes: MediaType[]
  storeCount: number
  lineItems: EstimateLineItem[]
  total: number
  storeNames: string[]
}

function formatZAR(amount: number): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    maximumFractionDigits: 0,
  }).format(amount)
}

export function generateEstimatePDF(data: EstimateData) {
  const refNumber = `GV-${Date.now().toString(36).toUpperCase()}`
  const today = new Date().toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  const storeListHTML = data.storeNames
    .map(
      (name) =>
        `<span style="display:inline-block;background:#f5f5f0;border:1px solid #e5e5e0;border-radius:2px;padding:2px 8px;margin:2px 4px 2px 0;font-size:11px;color:#444;">${name}</span>`,
    )
    .join('')

  const lineItemsHTML = data.lineItems
    .map(
      (item) => `
      <tr>
        <td style="padding:10px 12px;border-bottom:1px solid #e5e5e0;font-size:13px;">${item.mediaType}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #e5e5e0;font-size:13px;text-align:center;">${item.storeCount}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #e5e5e0;font-size:13px;text-align:right;">${formatZAR(item.unitPrice)}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #e5e5e0;font-size:13px;text-align:right;font-weight:600;">${formatZAR(item.subtotal)}</td>
      </tr>`,
    )
    .join('')

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Cost Estimate — ${data.campaignName || data.brandName}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    
    * { margin: 0; padding: 0; box-sizing: border-box; }
    
    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
      color: #1a1a1a;
      background: white;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    @page {
      size: A4;
      margin: 0;
    }
    
    .page {
      width: 210mm;
      min-height: 297mm;
      margin: 0 auto;
      padding: 0;
      position: relative;
    }
    
    @media print {
      .page { width: 100%; min-height: auto; }
      .no-print { display: none !important; }
    }
    
    @media screen {
      body { background: #e5e5e5; padding: 20px; }
      .page { box-shadow: 0 2px 20px rgba(0,0,0,0.15); }
    }
  </style>
</head>
<body>
  <div class="page">
    <!-- Header Bar -->
    <div style="background:#1a1a1a;padding:28px 40px;display:flex;align-items:center;justify-content:space-between;">
      <div style="display:flex;align-items:center;gap:16px;">
        <img src="https://cdn.magicpatterns.com/uploads/p8LJ2RFGdVthnquHL1oesF/GV_Icon.png" alt="Grapevine" style="height:36px;width:auto;filter:brightness(10);" />
        <div>
          <div style="color:white;font-size:18px;font-weight:700;letter-spacing:-0.3px;">Grapevine Media</div>
          <div style="color:#999;font-size:11px;font-weight:500;letter-spacing:0.5px;text-transform:uppercase;">Retail Media Platform</div>
        </div>
      </div>
      <div style="text-align:right;">
        <div style="color:white;font-size:13px;font-weight:600;">COST ESTIMATE</div>
        <div style="color:#999;font-size:11px;margin-top:2px;">${refNumber}</div>
      </div>
    </div>

    <!-- Sub-header -->
    <div style="background:#f5f5f0;border-bottom:1px solid #e5e5e0;padding:20px 40px;display:flex;justify-content:space-between;align-items:center;">
      <div>
        <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.8px;color:#888;font-weight:600;">Prepared For</div>
        <div style="font-size:18px;font-weight:700;color:#1a1a1a;margin-top:4px;">${data.brandName}</div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.8px;color:#888;font-weight:600;">Date</div>
        <div style="font-size:14px;font-weight:500;color:#1a1a1a;margin-top:4px;">${today}</div>
      </div>
    </div>

    <!-- Content -->
    <div style="padding:32px 40px;">
      
      <!-- Campaign Details -->
      <div style="margin-bottom:28px;">
        <div style="font-size:12px;text-transform:uppercase;letter-spacing:0.8px;color:#888;font-weight:600;margin-bottom:16px;">Campaign Details</div>
        <table style="width:100%;border-collapse:collapse;">
          <tr>
            <td style="padding:8px 0;font-size:13px;color:#666;width:140px;">Campaign Name</td>
            <td style="padding:8px 0;font-size:13px;font-weight:600;color:#1a1a1a;">${data.campaignName || '—'}</td>
          </tr>
          <tr>
            <td style="padding:8px 0;font-size:13px;color:#666;">Period</td>
            <td style="padding:8px 0;font-size:13px;font-weight:600;color:#1a1a1a;">Week ${data.weekNumber}${data.endWeekNumber ? ` – ${data.endWeekNumber}` : ''} ${data.weekLabel ? `(${data.weekLabel})` : ''}</td>
          </tr>
          <tr>
            <td style="padding:8px 0;font-size:13px;color:#666;">Media Types</td>
            <td style="padding:8px 0;font-size:13px;font-weight:600;color:#1a1a1a;">${data.mediaTypes.join(', ')}</td>
          </tr>
          <tr>
            <td style="padding:8px 0;font-size:13px;color:#666;vertical-align:top;">Stores (${data.storeCount})</td>
            <td style="padding:8px 0;font-size:13px;font-weight:500;color:#1a1a1a;line-height:1.6;">${storeListHTML}</td>
          </tr>
        </table>
      </div>

      <!-- Divider -->
      <div style="border-top:2px solid #1a1a1a;margin-bottom:24px;"></div>

      <!-- Pricing Table -->
      <div style="margin-bottom:24px;">
        <div style="font-size:12px;text-transform:uppercase;letter-spacing:0.8px;color:#888;font-weight:600;margin-bottom:16px;">Investment Breakdown</div>
        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr style="background:#f5f5f0;">
              <th style="padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:#666;font-weight:600;border-bottom:2px solid #ddd;">Media Type</th>
              <th style="padding:10px 12px;text-align:center;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:#666;font-weight:600;border-bottom:2px solid #ddd;">Stores</th>
              <th style="padding:10px 12px;text-align:right;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:#666;font-weight:600;border-bottom:2px solid #ddd;">Unit Price</th>
              <th style="padding:10px 12px;text-align:right;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:#666;font-weight:600;border-bottom:2px solid #ddd;">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            ${lineItemsHTML}
          </tbody>
        </table>
      </div>

      <!-- Total -->
      <div style="background:#1a1a1a;padding:16px 20px;display:flex;justify-content:space-between;align-items:center;margin-bottom:32px;">
        <div style="color:white;font-size:14px;font-weight:600;">TOTAL ESTIMATED INVESTMENT ${data.endWeekNumber ? `(for ${data.endWeekNumber - data.weekNumber + 1} weeks)` : '(per week)'}</div>
        <div style="color:white;font-size:24px;font-weight:700;letter-spacing:-0.5px;">${formatZAR(data.total)}</div>
      </div>

      <!-- Important Notice -->
      <div style="background:#fffbeb;border:1px solid #f59e0b;border-radius:4px;padding:14px 16px;margin-bottom:24px;">
        <div style="font-size:12px;font-weight:700;color:#92400e;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;">⚠ Important — Cost Estimate Only</div>
        <p style="font-size:12px;color:#78350f;line-height:1.6;margin:0;">
          This is a cost estimate. Your campaign is only confirmed once Woolworths has approved your booking and you have provided a purchase order to Grapevine.
        </p>
      </div>

      <!-- Terms -->
      <div style="margin-bottom:32px;">
        <div style="font-size:12px;text-transform:uppercase;letter-spacing:0.8px;color:#888;font-weight:600;margin-bottom:12px;">Terms & Conditions</div>
        <div style="font-size:11px;color:#666;line-height:1.7;">
          <p style="margin-bottom:6px;">1. This estimate is valid for 14 days from the date of issue.</p>
          <p style="margin-bottom:6px;">2. Your campaign is only confirmed once Woolworths has approved your booking and you have provided a purchase order to Grapevine.</p>
          <p style="margin-bottom:6px;">3. Final pricing is subject to availability confirmation and may vary based on seasonal demand.</p>
          <p style="margin-bottom:6px;">4. Artwork must be submitted at least 5 business days before the campaign start date.</p>
          <p style="margin-bottom:6px;">5. Cancellations within 7 days of the campaign start date may incur a 50% cancellation fee.</p>
          <p>6. All prices are exclusive of VAT unless otherwise stated.</p>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div style="position:absolute;bottom:0;left:0;right:0;border-top:1px solid #e5e5e0;padding:20px 40px;display:flex;justify-content:space-between;align-items:center;">
      <div style="display:flex;align-items:center;gap:12px;">
        <img src="https://cdn.magicpatterns.com/uploads/p8LJ2RFGdVthnquHL1oesF/GV_Icon.png" alt="Grapevine" style="height:20px;width:auto;opacity:0.5;" />
        <span style="font-size:10px;color:#999;">Grapevine Media (Pty) Ltd</span>
      </div>
      <div style="display:flex;align-items:center;gap:20px;">
        <span style="font-size:10px;color:#999;">info@grapevinemedia.co.za</span>
        <span style="font-size:10px;color:#999;">grapevinemedia.co.za</span>
      </div>
      <div style="display:flex;align-items:center;gap:8px;">
        <span style="font-size:10px;color:#bbb;">In partnership with</span>
        <img src="https://cdn.magicpatterns.com/uploads/4YqtTeUHiVXRLELZ1R2DtX/4dngqz0syxbaj28.png" alt="Woolworths" style="height:16px;width:auto;opacity:0.5;" />
      </div>
    </div>
  </div>

  <!-- Print Button (screen only) -->
  <div class="no-print" style="text-align:center;padding:20px;">
    <button onclick="window.print()" style="background:#1a1a1a;color:white;border:none;padding:12px 32px;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit;">
      Download as PDF
    </button>
  </div>
</body>
</html>`

  const printWindow = window.open('', '_blank')
  if (printWindow) {
    printWindow.document.write(html)
    printWindow.document.close()
  }
}
